import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BsClock } from "react-icons/bs";
import UiButton from "../../../../components/button/Button";
import UiLink from "../../../../components/link/Link.component";
import UiPhoneCaller from "../../../../components/phone-caller/PhoneCaller.component";
import UiTag from "../../../../components/tag/Tag.component";
import { AlertReason } from "../../../../models/entities/alert.model";
import { Task } from "../../../../models/entities/task.model";
import { TaskRow } from "../../../../models/pages/task-row.model";
import { Formatter } from "../../../../services/formatter/formatter.service";
import { HelperService } from "../../../../services/helpers/helper.service";
import {
	formatOutOfRangeAverage,
	getAlertDetails,
	getAlertReason,
	parseAlertMetadata,
} from "../../../../utils/alert-details";
import { buildAlertPriorityTag } from "../../../../utils/alert-priority";
import {
	getAlertParticipant,
	getAlertParticipantPhone,
	getAlertParticipantStatus,
} from "../worklist.service";

/**
 * One critical alert or consent task in the Home worklist (KAN-2553, KAN-2647).
 *
 * Every label, badge and formatter here is the one the Requires attention
 * surfaces already use, so the surfaces can never disagree about what an
 * alert is called or how urgent it looks. Actions are the platform's shared
 * Call control plus the Requires attention table's own Edit and Resolve
 * buttons, built from the same TaskRow class so they can never drift.
 *
 * Per Maria's delta 2 the sub-line shows: what fired, the priority, and the
 * alert date (in the same format as the alerts table). The triggering reading
 * value ("BP 182/98" in the mockup) is NOT shown — it is not recorded on the
 * alert today, and she deferred it to v2 rather than take the three-repo
 * backend change.
 *
 * KAN-2641: one row component for both sections (critical + SOOR) so style
 * matches exactly (Maria CR-b). The variant prop switches CSS classes, IDs
 * and content (the average label for SOOR); legacy rows without metadata
 * omit the average.
 *
 * KAN-2647: consent variant adds a blue edge and displays the participant
 * status (ACTIVE/PROSPECTIVE/...) when available.
 */

export interface CriticalAlertRowProps {
	alert: Task;
	/** Page-wide row number, 1-based — continues across worklist sections. */
	index: number;
	onResolve: (alert: Task) => void;
	onEdit: (alert: Task) => void;
	isResolving: boolean;
	/** Visual variant: "critical" (red edge, default), "soor" (orange edge), or "consent" (blue edge). */
	variant?: "critical" | "soor" | "consent";
}

const CriticalAlertRow = ({
	alert,
	index,
	onResolve,
	onEdit,
	isResolving,
	variant = "critical",
}: CriticalAlertRowProps) => {
	const { t } = useTranslation("common");

	const participant = getAlertParticipant(alert);
	const routeId = HelperService.getParticipantRouteId(participant.id, participant.kannactId);
	const phone = getAlertParticipantPhone(alert);
	const alertDate = alert.taskDate ?? alert.createdOn;

	const idPrefix =
		variant === "soor"
			? "soor-alert"
			: variant === "consent"
				? "consent-task"
				: "critical-alert";

	// The Requires attention table's own buttons, built by the same class that
	// builds them for the table (TaskRow.actionsTemplate) rather than restyled
	// copies — so Edit and Resolve can never drift between the two surfaces.
	const actions = useMemo(
		() =>
			new TaskRow(
				alert,
				() => onEdit(alert),
				() => onResolve(alert)
			).actionsTemplate,
		[alert, onEdit, onResolve]
	);

	const outOfRangeEntries = useMemo(
		() => (variant === "soor" ? parseAlertMetadata(alert.metadata) : null),
		[variant, alert.metadata]
	);

	return (
		<li
			className={`worklist-row${variant === "soor" ? " worklist-row--soor" : ""}${
				variant === "consent" ? " worklist-row--consent" : ""
			}`}
			data-testid={`${idPrefix}-row-${alert.id}`}
		>
			<span className="worklist-row__index">{index}</span>

			<div className="worklist-row__body">
				<div className="worklist-row__title">
					{/* Per Maria (KAN-2554): ONLY the Kannact ID is the link, the name is
						plain text — the same pattern as the rest of the platform. */}
					<span className="worklist-row__participant">
						{participant.kannactId &&
							(routeId ? (
								<UiLink
									id={`${idPrefix}-participant-${alert.id}`}
									to={`/participant/${routeId}/dashboard`}
									label={participant.kannactId}
								/>
							) : (
								<span className="kannact-id">{participant.kannactId}</span>
							))}
						{participant.name && <span className="full-name">{participant.name}</span>}
					</span>
					<UiTag {...buildAlertPriorityTag(alert.severity)} />
				</div>

				<div className="worklist-row__meta">
					<span className="worklist-row__what-fired">
						{getAlertDetails(t, alert.reason as AlertReason, alert.details as string) ||
							getAlertReason(t, alert.reason as string)}
					</span>
					{variant === "consent" && getAlertParticipantStatus(alert) && (
						<span
							className="worklist-row__status"
							data-testid={`${idPrefix}-status-${alert.id}`}
						>
							{t(`ENUMS.PARTICIPANT_STATUS.${getAlertParticipantStatus(alert)}`)}
						</span>
					)}
					{outOfRangeEntries && (
						<span
							className="worklist-row__average"
							data-testid={`${idPrefix}-average-${alert.id}`}
						>
							{outOfRangeEntries.map(formatOutOfRangeAverage).join(", ")}
						</span>
					)}
					{alertDate && (
						<span className="worklist-row__age">
							<BsClock />
							{Formatter.date(alertDate)}
						</span>
					)}
				</div>
			</div>

			<div className="worklist-row__actions">
				{/* Delta 3: the platform's shared call control, not a bespoke button.
				    Hidden rather than dead when the snapshot carries no number. */}
				{phone && (
					<UiPhoneCaller
						phoneNumber={phone}
						name={participant.name}
						size="p-button-xs"
					/>
				)}

				{actions.map((action) => {
					// The table's ids ("editAction"/"resolveAction") are per-row there
					// too; here the rows are all in one list, so make them unique.
					const name = action.id === "resolveAction" ? "resolve" : "edit";
					return (
						<UiButton
							key={action.id}
							{...action}
							type="button"
							id={`${idPrefix}-${name}-${alert.id}`}
							testId={`${idPrefix}-${name}-${alert.id}`}
							disabled={action.id === "resolveAction" ? isResolving : action.disabled}
							onClick={() => action.onClick?.()}
						/>
					);
				})}
			</div>
		</li>
	);
};

export default CriticalAlertRow;
